import { useEffect } from 'react';

import {
  notificationsApi2,
  useLazyGetNotificationsQuery,
} from '@app/api/Notifications';
import type { ModelNotification } from '@app/api/Notifications';
import { INFO } from '@app/constants/alerts';
import { useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { useNotificationClickHandler } from '@app/shared/components/NotificationsBell/hooks/useNotificationClickHandler';
import { readPopupNotificationsEnabled } from '@app/shared/components/NotificationsBell/lib';
import { useAppDispatch } from '@app/store';
import {
  addWsEventListener,
  createWSConnection,
  subscribeToWsReconnect,
} from '@app/utils/createWSConnection';

// Подтверждено вживую на dev (2026-08-27) через отладочный лог реального
// пейлоада: бэк шлёт только UnreadCountChanged (ReceiveNotification из
// US 10538 убрали из документации). Пейлоад — { isSuccess, errorMessage,
// deliveryId, data, jsonData }, и data/jsonData — НЕ сам ModelNotification,
// а обёртка над ним:
//   data (camelCase):  { unreadCount, notification: { id, eventType, ... } }
//   jsonData (строка, PascalCase): { "UnreadCount": n, "Notification": {...} }
// jsonData.Notification совпадает с ModelNotification один в один (Id/Title/
// Message/...), поэтому разбираем именно его — и заодно берём UnreadCount
// оттуда же, обновляя кеш счётчика локально (updateQueryData), без похода в
// GetUnreadCount. Если пейлоад не распарсился (не JSON, нет нужных полей —
// например, старый формат с голым числом или бэк не задеплоен на стенде),
// откатываемся на прежний путь: инвалидируем UnreadCount (рефетч через REST)
// и подтягиваем последнее уведомление через GetNotifications?page=1&pageSize=1
// для тоста.
//
// Тег Notifications (GetNotifications, список в модалке) при обычном пуше
// нигде не инвалидируется: рефетч списка при открытой модалке — лишний
// сетевой запрос, а нужное нам содержимое уже есть в пуше. Список и так
// перезапрашивается заново при каждом открытии модалки
// (refetchOnMountOrArgChange). Инвалидируем оба тега только на реконнекте —
// там мы могли пропустить не одно уведомление, а несколько сразу.
//
// Это также предполагает, что UnreadCountChanged сейчас растёт только при
// появлении новых уведомлений: пометка прочитанным (которая уменьшала бы
// счётчик) в интерфейсе пока не реализована (см. обсуждение в MR !4805) —
// если её добавят, здесь нужно будет отличать рост счётчика от уменьшения,
// чтобы не показывать тост на прочтение.
const UNREAD_COUNT_CHANGED_EVENT = 'UnreadCountChanged';
const LATEST_NOTIFICATION_PAGE_SIZE = 1;
// Компактный вариант info-уведомления, стили — NotificationsBell.scss.
const TOAST_CLASS_NAME = 'notification-toast';
// Тост появляется в левом нижнем углу.
const TOAST_PLACEMENT = 'bottomLeft' as const;
// Через сколько тост скрывается сам. Отсчёт стартует, когда вкладка CRM
// становится видимой (см. scheduleToastDismiss): если пользователь ушёл на
// другую вкладку, тост висит и исчезает только через N секунд после
// возвращения. Через штатный duration antd этого не добиться — antd не
// смотрит на visibilityState, поэтому duration держим 0, а закрываем сами.
const TOAST_AUTO_HIDE_MS = 8000;

type UnreadCountChangedEnvelope = {
  jsonData?: string;
};

type UnreadCountChangedPayload = {
  UnreadCount: number;
  Notification: ModelNotification;
};

const isUnreadCountChangedPayload = (
  value: unknown
): value is UnreadCountChangedPayload =>
  Boolean(
    value &&
      typeof value === 'object' &&
      typeof (value as UnreadCountChangedPayload).UnreadCount === 'number' &&
      (value as UnreadCountChangedPayload).Notification &&
      typeof (value as UnreadCountChangedPayload).Notification.Id === 'number'
  );

// Кликабелен именно текст (по аналогии с LeadTechnicalMessage__link в
// истории лида), а не весь тост целиком — клик по пустой области карточки
// ничего не делает. Заголовок тоста идёт строкой через showNotification и
// ссылкой быть не может — кликабелен текст сообщения (description).
const NotificationToastText = ({
  text,
  onClick,
}: {
  text: string | null;
  onClick: () => void;
}) => (
  <span
    className="notification-toast-link"
    role="button"
    tabIndex={0}
    onClick={onClick}
    onKeyDown={(event) => {
      if (event.key === 'Enter' || event.key === ' ') onClick();
    }}
  >
    {text}
  </span>
);

const parsePushedPayload = (raw: unknown): UnreadCountChangedPayload | null => {
  if (!raw || typeof raw !== 'object') return null;

  const { jsonData } = raw as UnreadCountChangedEnvelope;
  if (typeof jsonData !== 'string') return null;

  try {
    const parsed: unknown = JSON.parse(jsonData);
    if (isUnreadCountChangedPayload(parsed)) return parsed;
  } catch {
    // jsonData — не JSON-объект ожидаемой формы (например, старый формат
    // с голым числом "167")
  }

  return null;
};

export const useNotificationsRealtime = () => {
  const dispatch = useAppDispatch();
  const { api, showNotification } = useNotification();
  const handleNotificationClick = useNotificationClickHandler();
  const { activeBranchId } = useUserPrint();
  const [fetchLatestNotification] = useLazyGetNotificationsQuery();

  useEffect(() => {
    const invalidateUnreadCount = () => {
      dispatch(notificationsApi2.util.invalidateTags(['UnreadCount']));
    };

    const invalidateOnReconnect = () => {
      dispatch(
        notificationsApi2.util.invalidateTags(['Notifications', 'UnreadCount'])
      );
    };

    // Тосты, которые уже показаны, но их таймер автоскрытия ещё не запущен:
    // вкладка была скрыта в момент показа. Ключ → id таймера, если запущен.
    const pendingToastKeys = new Set<string>();
    const dismissTimers = new Map<string, ReturnType<typeof setTimeout>>();

    const clearToastTimer = (key: string) => {
      pendingToastKeys.delete(key);
      const timer = dismissTimers.get(key);
      if (timer) {
        clearTimeout(timer);
        dismissTimers.delete(key);
      }
    };

    // Запускает отсчёт автоскрытия для тоста. Пока вкладка скрыта —
    // откладываем: таймер стартует в onVisibilityChange при возврате.
    const scheduleToastDismiss = (key: string) => {
      if (document.visibilityState !== 'visible') {
        pendingToastKeys.add(key);
        return;
      }

      pendingToastKeys.delete(key);
      dismissTimers.set(
        key,
        setTimeout(() => {
          dismissTimers.delete(key);
          api.destroy(key);
        }, TOAST_AUTO_HIDE_MS)
      );
    };

    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      pendingToastKeys.forEach((key) => scheduleToastDismiss(key));
    };

    document.addEventListener('visibilitychange', onVisibilityChange);

    // Тост — через showNotification в состоянии info (слева снизу, без
    // штатного автозакрытия antd), компактный вариант по className.
    // Скрытием управляем сами через scheduleToastDismiss, чтобы отсчёт шёл
    // от момента, когда пользователь реально видит вкладку. Свич
    // «Отображение всплывающих уведомлений» из окна «Уведомления» читается
    // в момент показа: счётчик и список он не затрагивает.
    const showNotificationToast = (notificationItem: ModelNotification) => {
      if (!readPopupNotificationsEnabled()) return;

      const notificationKey = `notification-${notificationItem.Id}`;

      const goToEntity = () => {
        clearToastTimer(notificationKey);
        api.destroy(notificationKey);
        handleNotificationClick(
          notificationItem.EntityType,
          notificationItem.EntityId
        );
      };

      showNotification(
        notificationItem.Title ?? '',
        INFO,
        <NotificationToastText
          text={notificationItem.Message}
          onClick={goToEntity}
        />,
        {
          key: notificationKey,
          className: TOAST_CLASS_NAME,
          placement: TOAST_PLACEMENT,
        }
      );

      scheduleToastDismiss(notificationKey);
    };

    const showLatestNotificationToast = async () => {
      if (!activeBranchId) return;

      try {
        const { Items } = await fetchLatestNotification({
          ActiveBranchId: activeBranchId,
          page: 1,
          pageSize: LATEST_NOTIFICATION_PAGE_SIZE,
        }).unwrap();

        const latestNotification = Items?.[0];
        if (latestNotification) showNotificationToast(latestNotification);
      } catch {
        // Тост — это улучшение поверх бейджа/списка, а не источник истины:
        // если REST-запрос не удался, бейдж всё равно обновится через
        // invalidateUnreadCount() ниже, просто без всплывающей подсказки.
      }
    };

    const handleUnreadCountChanged = (raw: unknown) => {
      const pushedPayload = parsePushedPayload(raw);

      if (pushedPayload) {
        // Счётчик уже известен из пуша (pushedPayload.UnreadCount), но
        // notificationsApi2.util.updateQueryData не типизируется здесь:
        // reducers в src/store/rtkQuery.ts собран как Record<string, any>,
        // из-за чего RootState теряет info о слайсах v2-API и updateQueryData
        // не может вывести совместимый ThunkAction (нигде в проекте он
        // поэтому и не используется). Чинить типизацию всего стора ради
        // этого не стали — просто инвалидируем тег, это лёгкий одиночный
        // GET на число, а не список.
        invalidateUnreadCount();
        showNotificationToast(pushedPayload.Notification);
      } else {
        invalidateUnreadCount();
        void showLatestNotificationToast();
      }
    };

    let removeUnreadCountListener: (() => void) | null = null;
    let isEffectActive = true;

    // addWsEventListener подключает обработчик к реальному SignalR-хабу
    // (hubConnectionInstance.on(...)) только если соединение уже создано —
    // а PageProvider поднимает его в СВОЁМ эффекте (connectListenUser), который
    // из-за порядка выполнения React-эффектов (у детей раньше, чем у родителя)
    // отрабатывает ПОЗЖЕ эффекта этого хука. Без ожидания через createWSConnection
    // подписка молча регистрируется только в локальном реестре и никогда не
    // доходит до реального hub.on(...).
    createWSConnection(() => {
      if (!isEffectActive) return;

      removeUnreadCountListener = addWsEventListener(
        UNREAD_COUNT_CHANGED_EVENT,
        handleUnreadCountChanged
      );
    });

    const unsubscribeReconnect = subscribeToWsReconnect(invalidateOnReconnect);

    return () => {
      isEffectActive = false;
      removeUnreadCountListener?.();
      unsubscribeReconnect();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      dismissTimers.forEach(clearTimeout);
      dismissTimers.clear();
      pendingToastKeys.clear();
    };
  }, [
    dispatch,
    api,
    showNotification,
    handleNotificationClick,
    activeBranchId,
    fetchLatestNotification,
  ]);
};
